import React from 'react'
import PropTypes from 'prop-types'
import { Player as ReactPlayer } from 'video-react'
import styled from 'styled-components'
import flvjs from 'flv.js'
import { DisplayAlert } from './Video.components'

/**
 * react component wrap flv.js
 */

let TIMER_ID = null
const TIMER = 20000

let videoError = { state: false, message: '' }

const FakePlayer = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
`

/**
 * hide pause event
 */
const Player = styled(ReactPlayer)`
  display: flex;
  justify-content: center;

  .video-react-play-control,
  .video-react-control-bar {
    display: none;
  }

  video {
    pointer-events: none;
  }
`

class FLVSource extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    /**
     * media URL, can be starts with 'https(s)' or 'ws(s)' (WebSocket)
     */
    url: PropTypes.string,
    /**
     * media type, 'flv' or 'mp4'
     */
    type: PropTypes.oneOf(['flv', 'mp4']).isRequired,
    /**
     * whether the data source is a **live stream**
     */
    isLive: PropTypes.bool,
    /**
     * whether to enable CORS for http fetching
     */
    cors: PropTypes.bool,
    /**
     * whether to do http fetching with cookies
     */
    withCredentials: PropTypes.bool,
    /**
     * whether the stream has audio track
     */
    hasAudio: PropTypes.bool,
    /**
     * whether the stream has video track
     */
    hasVideo: PropTypes.bool,
    /**
     * total media duration, in milliseconds
     */
    duration: PropTypes.bool,
    /**
     * total file size of media file, in bytes
     */
    filesize: PropTypes.number,
    /**
     * Optional field for multipart playback, see MediaSegment
     */
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * indicates segment duration in milliseconds
         */
        duration: PropTypes.number.isRequired,
        /**
         * indicates segment file size in bytes
         */
        filesize: PropTypes.number,
        /**
         * indicates segment file URL
         */
        url: PropTypes.string.isRequired,
      })
    ),
    /**
     * @see https://github.com/Bilibili/flv.js/blob/master/docs/api.md#config
     */
    config: PropTypes.object,
    /**
     * The poster attribute specifies an image to be shown while the video is downloading, or until the user hits the play button.
     * If this is not included, the first frame of the video will be used instead.
     */
    poster: PropTypes.string,
  }

  state = {
    initial: false,
  }

  static defaultProps = {
    type: 'flv',
    isLive: true,
    cors: true,
    hasAudio: false,
    hasVideo: true,
    // enableWorker: true,
    // enableStashBuffer: false,
    // stashInitialSize: 128, //緩存
  }

  initFlv = () => {
    if (!flvjs.isSupported()) {
      return
    }

    const { video, handleError } = this.props

    this.flvPlayer = flvjs.createPlayer({ ...this.props })
    this.flvPlayer.attachMediaElement(video)

    this.flvPlayer.on(flvjs.Events.ERROR, err => {
      videoError.state = true

      switch (err) {
        case 'NetworkError':
          videoError.message = '網路連線不可用，請稍後重試'
          break
        case 'MediaError':
          videoError.message = '載入有誤，請確認影片來源'
          break
        default:
          videoError.message = '出現無預期的錯誤'
      }

      handleError(videoError)
    })

    video.addEventListener('loadstart', () => {
      video.play().catch(err => console.warn('[catch]', err))
    })

    this.flvPlayer.on(flvjs.Events.METADATA_ARRIVED, () => {
      TIMER_ID && clearTimeout(TIMER_ID)
    })

    this.setState({ initial: true })
  }

  startVideo = () => {
    if (!flvjs.isSupported()) {
      return
    }

    this.flvPlayer.load()
  }

  componentDidMount() {
    const { url, handleError } = this.props

    if (url) {
      this.initFlv()

      // console.log('[props]', this.props)

      TIMER_ID = setTimeout(() => {
        videoError.state = true
        videoError.message = '載入有誤，請稍後再試'
        handleError(videoError)
      }, TIMER)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.initial !== this.state.initial) {
      if (this.state.initial) {
        this.startVideo()
      }
    }

    if (prevProps.player.paused !== this.props.player.paused) {
      // console.log('[paused]', this.props.player.paused)

      if (this.props.player.paused) {
        this.flvPlayer.unload()
      }
      if (!this.props.player.paused) {
        this.startVideo()
      }
    }
  }

  componentWillUnmount() {
    if (this.flvPlayer) {
      this.flvPlayer.unload()
      this.flvPlayer.detachMediaElement()
      this.flvPlayer.destroy()
      this.flvPlayer = null
      clearTimeout(TIMER_ID)
    }
  }

  render() {
    return <source src={this.props.url} />
  }
}

export default function FlvPlayer({ url, isPlaying, ...props }) {
  const [error, setError] = React.useState({ state: false, message: '' })

  return (
    <>
      {!error.state ? (
        isPlaying ? (
          <Player fluid={false} width="100%" height="100%" {...props}>
            <FLVSource isVideoChild url={url} handleError={setError} />
          </Player>
        ) : (
          <FakePlayer />
        )
      ) : (
        <DisplayAlert message={error.message} />
      )}
    </>
  )
}
